// CP Site Visit approval view (a pill inside the CP Sourcing tab). Lists CP site
// visits in the logged-in user's hierarchy and lets the direct parent (approver)
// open each one and Verify/Reject. UI mirrors the Site Visit Approval tab
// (same DataGrid styling, status chips, and View Details action).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, TextField, CircularProgress, InputAdornment, useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getCpSiteVisitsList, verifyCpSiteVisit } from "../../Services/CpSourcingApis";
import ViewCpSiteVisitDetails from "../../Modals/ViewCpSiteVisitDetails/ViewCpSiteVisitDetails";
import Button from "../../Subcomponents/Button";
import LeadsFilterPanel from "../../Subcomponents/LeadsFilterPanel";
import ProjectIcon from "../../Assets/Images/Sustenance/ProjectIcon.svg";
import cssClasses from "../SiteVisit/SiteVisit.module.css";
import MobileFilterBar from "./Mobile/MobileFilterBar";
import MobileFilterSheet from "./Mobile/MobileFilterSheet";
import ActiveFilterChips from "./Mobile/ActiveFilterChips";
import SiteVisitCard from "./Mobile/SiteVisitCard";
import mobileCss from "./Mobile/cpSourcingMobile.module.css";
// Three-state verification palette, shared with the mobile card's status pill.
import { statusPalette } from "./cpSourcingTheme";

const MOBILE_PAGE_SIZE = 10;

const StatusChip = ({ status }) => {
  const p = statusPalette(status);
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "0.6rem", padding: "0.3rem 1.2rem",
      borderRadius: "1.6rem", fontSize: "1.3rem", fontFamily: "Inter-Medium",
      backgroundColor: p.bg, color: p.color, border: `1px solid ${p.border}`, whiteSpace: "nowrap",
    }}>
      <Box component={"span"} sx={{ width: "0.7rem", height: "0.7rem", borderRadius: "50%", backgroundColor: p.dot, flexShrink: 0 }} />
      {p.label}
    </Box>
  );
};

// Copied from the Site Visit Approval tab so the grid looks identical.
const dataGridStyles = {
  "&.MuiDataGrid-root .MuiDataGrid-cell:focus": { outline: "none" },
  border: "1px solid #EAECF0 !important",
  fontWeight: "400",
  fontSize: "1.4rem",
  lineHeight: "2rem",
  color: "#667085",
  fontFamily: "Inter-Medium",
  borderRadius: "8px",
  '& div[data-colindex="1"]': { color: "#101828", fontWeight: "500" },
  "& .MuiDataGrid-virtualScrollerRenderZone": {
    "& .MuiDataGrid-row": { borderBottom: "1px solid #EAECF0", background: "#FFFFFF", borderRadius: "1rem" },
    "& .MuiDataGrid-row.Mui-selected:hover": { borderBottom: "1px solid #EAECF0", backgroundColor: "#FFFFFF" },
  },
  "& .MuiDataGrid-iconSeparator ": { display: "none" },
  "& .MuiDataGrid-columnHeader:hover": { "& .MuiDataGrid-menuIcon": { visibility: "hidden" } },
  "&  .MuiDataGrid-columnHeaders": {
    background: "#F9FAFB",
    border: "1px solid #EAECF0",
    marginBottom: "1rem",
    borderBottom: "6px solid #EAECF0",
  },
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .MuiDataGrid-footerContainer": {
    "& .MuiTablePagination-displayedRows ": { display: "none" },
    "& .MuiDataGrid-selectedRowCount": { visibility: "hidden " },
  },
  "&  [title='Sort']": { display: "none", cursor: "none" },
  "& [title='Go to previous page']": { border: "1px solid #EAECF0", borderRadius: "0.8rem", marginRight: "0.5rem" },
  "& [title='Go to next page']": { border: "1px solid #EAECF0", borderRadius: "0.8rem", marginRight: "0.5rem" },
  "& [title='Go to previous page']:after": { content: "'Previous'", marginLeft: "1rem" },
  "& [title='Go to next page']:before": { content: "'Next'", marginRight: "1rem" },
  "& .MuiButtonBase-root": { fontSize: "1.6rem" },
  "& .MuiDataGrid-cell:focus-within": { outline: "none !important" },
  "& .MuiDataGrid-cell": { padding: "0 2rem", "&:focus": { outline: "none" } },
  "& .MuiDataGrid-columnHeader": { padding: "0 1rem", "&:focus": { outline: "none" } },
};

// Status filter options — same three the dropdown offered, same wording.
const STATUS_OPTIONS = [
  { id: "pending", label: "Verification Pending" },
  { id: "approved", label: "Verified" },
  { id: "rejected", label: "Rejected" },
];

// Map a list row into the shape ViewCpSiteVisitDetails expects.
const toModalData = (row) => ({
  cpName: row.cpName,
  cpDisplayId: row.cpDisplayId,
  projectName: row.projectName,
  mode: row.mode,
  contactNumber: row.contactNumber,
  photoUrl: row.photoUrl,
  sitevisitdate: row.dateTime,
  startdatetime: row.startDateTime,
  enddatetime: row.endDateTime,
  remarks: row.remarks,
  status: row.status,
  verifiedByName: row.verifiedByName,
  approverName: row.approverName,
  comment: row.comment,
  sitevisitid: row.cpSiteVisitId,
  cpid: row.cpId,
});

export default function CpSiteVisitApprovalView({ userId, visibility, teamIds = [], allMode = false, approveAccess = false, viewAccess = false }) {
  const loggedInUser = useSelector((s) => s?.Auth?.user);
  const meId = userId || loggedInUser?.userId || loggedInUser?.id;

  const inHierarchy = (row) => allMode || (teamIds || []).includes(row?.approverId);
  const isParent = (row) => row?.approverId === meId;

  // Hybrid: the CP's parent verifies/rejects BY DEFAULT. Granting "Approve" extends
  // that to a user within their scope; granting ONLY "View Details" downgrades even
  // the parent to view-only; granting nothing leaves the parent's default in place.
  const canApproveRow = (row) =>
    approveAccess ? inHierarchy(row) : viewAccess ? false : isParent(row);
  const canViewRow = (row) =>
    viewAccess || approveAccess ? inHierarchy(row) : isParent(row);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // Status filter, multi-select like every other Leads-panel filter.
  // pending = what the checkboxes show; applied = what actually filters.
  const [pendingStatuses, setPendingStatuses] = useState([]);
  const [appliedStatuses, setAppliedStatuses] = useState([]);
  const [selected, setSelected] = useState(null); // row open in the modal
  // Same breakpoint the Partners mobile view uses (ChannelPartner.jsx).
  const isMobile = useMediaQuery("(max-width:820px)");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobilePage, setMobilePage] = useState(0);

  const toggleStatus = (value) =>
    setPendingStatuses((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );

  // One config, handed to the desktop panel and the mobile sheet alike.
  const statusFilterCategories = [
    {
      label: "Status",
      options: STATUS_OPTIONS,
      selectedIds: pendingStatuses,
      onToggle: toggleStatus,
      emptyMessage: "No Statuses Found",
    },
  ];

  const clearStatuses = () => { setPendingStatuses([]); setAppliedStatuses([]); };

  // Chips mirror the *applied* statuses. Removing one applies immediately and
  // drops it from pending too, so reopening the sheet agrees with the chips.
  const activeStatusChips = appliedStatuses.map((id) => ({
    id,
    label: STATUS_OPTIONS.find((o) => o.id === id)?.label || String(id),
  }));

  const removeStatusChip = (value) => {
    setPendingStatuses((prev) => prev.filter((s) => s !== value));
    setAppliedStatuses((prev) => prev.filter((s) => s !== value));
  };

  // The endpoint takes a single status, so one selection still filters
  // server-side exactly as before. Two or more can't be expressed as a param —
  // those fetch unfiltered and narrow client-side instead.
  const statusParam = appliedStatuses.length === 1 ? appliedStatuses[0] : undefined;

  // Applied statuses always narrow the rows here too. When the server already
  // filtered (single selection) every row matches, so this is a no-op; with two
  // or more it is what actually applies the filter.
  const visibleRows = useMemo(
    () =>
      appliedStatuses.length === 0
        ? rows
        : rows.filter((r) => appliedStatuses.includes(r.status)),
    [rows, appliedStatuses]
  );

  // The mobile card list pages client-side at the same size the desktop grid
  // uses, so both show the same rows in the same order.
  useEffect(() => { setMobilePage(0); }, [search, appliedStatuses]);

  const mobileLastPage = Math.max(0, Math.ceil(visibleRows.length / MOBILE_PAGE_SIZE) - 1);
  // Clamp on every render: verifying a row refetches, and a shrinking result
  // set must never strand the list on a page that no longer exists.
  const mobileCurrentPage = Math.min(Math.max(mobilePage, 0), mobileLastPage);
  const mobileRows = visibleRows.slice(
    mobileCurrentPage * MOBILE_PAGE_SIZE,
    mobileCurrentPage * MOBILE_PAGE_SIZE + MOBILE_PAGE_SIZE
  );

  const fetchRows = useCallback(async () => {
    if (!visibility) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getCpSiteVisitsList({
        userId: meId,
        scope: visibility.scope,
        userIds: visibility.userIds,
        search: search.trim() || undefined,
        status: statusParam,
      });
      if (res?.success) setRows(res.data || []);
      else setError(res?.message || "Failed to load CP site visits.");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load CP site visits.");
    } finally {
      setLoading(false);
    }
  }, [visibility, meId, search, statusParam]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 350);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const handleVerify = async (data, comment) => {
    try {
      const res = await verifyCpSiteVisit(data.cpid, data.sitevisitid, { userId: meId, status: "approved", comment });
      if (res?.success) {
        toast.success("CP site visit verified", { position: "top-center", autoClose: 3000 });
        setSelected(null);
        fetchRows();
      } else {
        toast.error(res?.message || "Could not verify the site visit", { position: "top-center", autoClose: 3000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not verify the site visit", { position: "top-center", autoClose: 3000 });
    }
  };

  const handleReject = async (data, comment) => {
    try {
      const res = await verifyCpSiteVisit(data.cpid, data.sitevisitid, { userId: meId, status: "rejected", comment });
      if (res?.success) {
        toast.success("CP site visit rejected", { position: "top-center", autoClose: 3000 });
        setSelected(null);
        fetchRows();
      } else {
        toast.error(res?.message || "Could not reject the site visit", { position: "top-center", autoClose: 3000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not reject the site visit", { position: "top-center", autoClose: 3000 });
    }
  };

  const columns = [
    {
      field: "Cp",
      headerName: "Channel Partner",
      flex: 2.2,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => (
        <Box className={cssClasses.SubContainer}>
          {params.row.photoUrl ? (
            <img
              style={{ width: "4rem", height: "4rem", objectFit: "cover", borderRadius: "50%", flexShrink: 0 }}
              src={process.env.REACT_APP_SUPABASE_BUCKET_PREFIX + params.row.photoUrl}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = ProjectIcon; }}
              alt={""}
            />
          ) : (
            <img style={{ width: "4rem", height: "4rem", objectFit: "cover", borderRadius: "50%", flexShrink: 0 }} src={ProjectIcon} alt={""} />
          )}
          <Box>
            <Box className={cssClasses.BoldTitle} whiteSpace={"break-spaces"}>
              {params?.row?.cpName ? params.row.cpName : "-"}
            </Box>
            <Box className={cssClasses.semiBoldTitle} whiteSpace={"break-spaces"}>
              {params?.row?.cpDisplayId ? `#${params.row.cpDisplayId}` : "-"}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: "cpSourceName",
      headerName: "CP Sourcing",
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Box className={cssClasses.BoldTitle} whiteSpace={"break-spaces"}>
          {params?.row?.cpSourceName ? params.row.cpSourceName : "-"}
        </Box>
      ),
    },
    {
      field: "VisitDateTime",
      headerName: "Visit Date & Time ",
      flex: 1.4,
      minWidth: 200,
      sortable: false,
      renderCell: (params) =>
        params.row.dateTime ? (
          <Box whiteSpace={"break-spaces"}>
            {new Date(Number(params.row.dateTime)).toDateString()},{" "}
            {new Date(Number(params.row.dateTime)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Box>
        ) : (
          <Box>-</Box>
        ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.3,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) =>
        canViewRow(params.row) ? (
          <Button
            style={{
              color: "#155EEF",
              backgroundColor: "#FFF",
              border: "1px solid #155EEF",
              fontSize: "1.4rem",
              lineHeight: "2rem",
            }}
            btnName={"View Details"}
            padding={"0.5rem 2.5rem"}
            clicked={() => setSelected(params.row)}
          />
        ) : (
          <Box sx={{ color: "#98A2B3", fontSize: "1.3rem", fontFamily: "Inter-Medium" }}>—</Box>
        ),
    },
  ];

  return (
    <Box className={cssClasses.wrapperPage}>
      <Box className={cssClasses.wrapperHeader}>
        <Box>
          <Box className={cssClasses.headerTitle}>CP Site Visit Approval</Box>
          <Box className={cssClasses.headerSubTitle}>Review and verify site visits done for your CPs.</Box>
        </Box>
      </Box>

      {/* On phones the search field and a filter icon share one line, matching
          the Partners mobile view; the desktop row is left untouched. */}
      {isMobile ? (
        <Box sx={{ mt: "16px" }}>
          <MobileFilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search CP name / id…"
            filterCount={appliedStatuses.length}
            onOpenFilters={() => setMobileFilterOpen(true)}
          />
          <ActiveFilterChips chips={activeStatusChips} onRemove={removeStatusChip} />
        </Box>
      ) : (
        <Box className={cssClasses.wrapperFilters}>
          <Box className={cssClasses.allLeadsTitle}>All CP Site Visits</Box>
          <Box sx={{ display: "flex", gap: "1.5rem", alignItems: "flex-end", flexWrap: "wrap" }}>
            <LeadsFilterPanel
              categories={statusFilterCategories}
              onClearPending={() => setPendingStatuses([])}
              onClear={clearStatuses}
              onApply={() => setAppliedStatuses(pendingStatuses)}
              /* this filter row is right-aligned (wrapperFilters is
                 space-between), so the panel has to grow leftwards */
              align="right"
            />
            <TextField
              size="small"
              placeholder="Search CP name / id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: "24rem", "& .MuiOutlinedInput-root": { borderRadius: "0.8rem", fontSize: "1.4rem", fontFamily: "Inter-Medium", background: "#FFF" } }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: "2rem", color: "#98A2B3" }} /></InputAdornment>) }}
            />
          </Box>
        </Box>
      )}

      {error && (
        <Box sx={{ color: "#B42318", fontSize: "1.4rem", fontFamily: "Inter-Medium", mb: "1rem" }}>{error}</Box>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", py: "4rem" }}><CircularProgress size={28} sx={{ color: "#155EEF" }} /></Box>
      ) : visibleRows.length === 0 ? (
        <Box className={cssClasses.noDataMsg} sx={{ py: "5rem" }}>
          <Box className={cssClasses.emptyTitle}>No CP site visit records were found</Box>
        </Box>
      ) : isMobile ? (
        /* Cards instead of a horizontally scrolling grid. Same rows, same
           order, same 10-per-page slicing the grid does. */
        <div className={`cpSourcingMobileRoot ${mobileCss.list}`}>
          {mobileRows.map((row) => (
            <SiteVisitCard
              key={row.cpSiteVisitId}
              row={row}
              canView={canViewRow(row)}
              onOpen={setSelected}
            />
          ))}

          {mobileLastPage > 0 && (
            <div className={mobileCss.pager}>
              <button
                type="button"
                className={`${mobileCss.btn} ${mobileCss.btnSecondary}`}
                disabled={mobileCurrentPage <= 0}
                onClick={() => setMobilePage(mobileCurrentPage - 1)}
              >
                Previous
              </button>
              <span className={mobileCss.pagerInfo}>
                {mobileCurrentPage + 1} / {mobileLastPage + 1}
              </span>
              <button
                type="button"
                className={`${mobileCss.btn} ${mobileCss.btnSecondary}`}
                disabled={mobileCurrentPage >= mobileLastPage}
                onClick={() => setMobilePage(mobileCurrentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      ) : (
        <Box sx={{ width: "100%", mt: "1.6rem" }}>
          <DataGrid
            autoHeight
            rows={visibleRows}
            columns={columns}
            getRowId={(r) => r.cpSiteVisitId}
            pageSize={10}
            rowsPerPageOptions={[10]}
            rowHeight={92}
            headerHeight={52}
            disableSelectionOnClick
            disableColumnMenu
            sx={[dataGridStyles]}
          />
        </Box>
      )}

      {isMobile && (
        <MobileFilterSheet
          open={mobileFilterOpen}
          categories={statusFilterCategories}
          onApply={() => setAppliedStatuses(pendingStatuses)}
          onClearAll={clearStatuses}
          onClose={() => setMobileFilterOpen(false)}
        />
      )}

      {selected && (
        <ViewCpSiteVisitDetails
          open={!!selected}
          handleClose={() => setSelected(null)}
          data={toModalData(selected)}
          canApprove={canApproveRow(selected)}
          onVerify={handleVerify}
          onReject={handleReject}
        />
      )}
    </Box>
  );
}
